import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Typography,
  Stack,
  LinearProgress,
  alpha,
  useTheme,
} from '@mui/material';
import { Timeline, AccessTime } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getPhaseColor, getRiskColor, getRiskLabel } from './utils';

const TimelineSection = ({ chartData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  if (!chartData || chartData.length === 0) {
    return null;
  }

  const timelineData = chartData.slice(-30).reverse();

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 3,
        background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.grey[50], 0.3)} 100%)`,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <CardContent sx={{ p: 3, pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`,
              }}
            >
              <Timeline sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.5, mb: 0.5 }}>
                {t('downtrend.timeline.title', 'Risk Timeline')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('downtrend.timeline.description', 'Risk and phase history over time')} ({timelineData.length} {t('downtrend.timeline.dataPoints', 'data points')})
              </Typography>
            </Box>
          </Stack>
        </CardContent>

        {/* Table */}
        <TableContainer>
          <Table size="medium" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  '& th': {
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    py: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  },
                }}
              >
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AccessTime sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t('downtrend.timeline.date')}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('downtrend.timeline.risk10d')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('downtrend.timeline.shortTerm', 'Short-term')}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('downtrend.timeline.risk20d')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('downtrend.timeline.mediumTerm', 'Medium-term')}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('downtrend.timeline.risk60d')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('downtrend.timeline.longTerm', 'Long-term')}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('downtrend.timeline.phase')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timelineData.map((row, idx) => {
                const phaseInfo = getPhaseColor(row.phase60);
                const isLatest = idx === 0;
                const risk10Color = getRiskColor(row.risk10);
                const risk20Color = getRiskColor(row.risk20);
                const risk60Color = getRiskColor(row.risk60);
                
                return (
                  <TableRow
                    key={idx}
                    hover
                    sx={{
                      transition: 'all 0.2s ease',
                      bgcolor: isLatest ? alpha(theme.palette.primary.main, 0.02) : 'transparent',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.action.hover, 0.3),
                      },
                      '& td': {
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        py: 1,
                        verticalAlign: 'middle',
                      },
                    }}
                  >
                    {/* Date Column */}
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography
                          variant="body2"
                          fontWeight={isLatest ? 600 : 500}
                          sx={{
                            color: isLatest ? theme.palette.primary.main : 'text.primary',
                            fontSize: '0.875rem',
                          }}
                        >
                          {row.date}
                        </Typography>
                        {isLatest && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.7rem',
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              width: 'fit-content',
                            }}
                          >
                            {t('downtrend.timeline.latest', 'Latest')}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    {/* Risk 10D Column */}
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            color: risk10Color,
                            fontSize: '0.875rem',
                            minWidth: 50,
                            textAlign: 'right',
                          }}
                        >
                          {row.risk10.toFixed(1)}%
                        </Typography>
                        <Box sx={{ flex: 1, maxWidth: 60 }}>
                          <LinearProgress
                            variant="determinate"
                            value={row.risk10}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              bgcolor: alpha(risk10Color, 0.08),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: risk10Color,
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500, minWidth: 60 }}>
                          {getRiskLabel(row.risk10, t)}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Risk 20D Column */}
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            color: risk20Color,
                            fontSize: '0.875rem',
                            minWidth: 50,
                            textAlign: 'right',
                          }}
                        >
                          {row.risk20.toFixed(1)}%
                        </Typography>
                        <Box sx={{ flex: 1, maxWidth: 60 }}>
                          <LinearProgress
                            variant="determinate"
                            value={row.risk20}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              bgcolor: alpha(risk20Color, 0.08),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: risk20Color,
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500, minWidth: 60 }}>
                          {getRiskLabel(row.risk20, t)}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Risk 60D Column */}
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            color: risk60Color,
                            fontSize: '0.875rem',
                            minWidth: 50,
                            textAlign: 'right',
                          }}
                        >
                          {row.risk60.toFixed(1)}%
                        </Typography>
                        <Box sx={{ flex: 1, maxWidth: 60 }}>
                          <LinearProgress
                            variant="determinate"
                            value={row.risk60}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              bgcolor: alpha(risk60Color, 0.08),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: risk60Color,
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500, minWidth: 60 }}>
                          {getRiskLabel(row.risk60, t)}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Phase Column */}
                    <TableCell align="center">
                      {phaseInfo ? (
                        <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="center">
                          {React.cloneElement(phaseInfo.icon, {
                            sx: { fontSize: 16, color: phaseInfo.color },
                          })}
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              color: phaseInfo.color,
                              fontSize: '0.8rem',
                            }}
                          >
                            {t(phaseInfo.labelKey)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: 500,
                          }}
                        >
                          {t('downtrend.riskLabels.na')}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
  );
};

export default TimelineSection;

